import React, { useEffect, useState } from 'react';
import { Row, Col, Card, Divider, Typography, Tooltip } from 'antd';
import UserAvatar from '../../common/UserAvatar';
import { getAllGroups } from '../../../reducers/campaign/resources';
import templateResources from '../../../reducers/templates/resources';
import { formatInUserTime, formatSchedule } from '../../../utils/campaign-timezone';

const { Text, Paragraph } = Typography;

const styles = {
    detailLabel: {
        fontSize: 12,
        color: 'var(--gh-text-secondary, #607D8B)',
        display: 'block',
        marginBottom: 2
    },
    detailValue: {
        fontSize: 13,
        color: 'var(--gh-text-primary, #263238)',
        fontWeight: 500,
        minHeight: 20
    },
    messageBox: {
        border: '1px solid var(--gh-gray-200, #E6E6E6)',
        background: 'var(--gh-gray-50, #FAFAFA)',
        borderRadius: 8,
        padding: '8px 10px',
        minHeight: 56,
        whiteSpace: 'pre-wrap'
    }
};

const DetailField = ({ label, value }) => (
    <div style={{ minHeight: 42 }}>
        <span style={styles.detailLabel}>{label}</span>
        <div style={styles.detailValue}>{value || '--'}</div>
    </div>
);

const labelFromLookup = (map, id) => {
    if (!id) return '--';
    return map[id] || id;
};

const CampaignDetailsCard = ({ campaignDetails, children, hideTemplate, hideCampaignStatus, hideMessage }) => {
    const [lookupMaps, setLookupMaps] = useState({ groupsById: {}, templatesById: {} });

    useEffect(() => {
        let mounted = true;

        const loadLookups = async () => {
            try {
                const [groupsRes, templatesRes] = await Promise.all([
                    getAllGroups(),
                    templateResources.getTemplates()
                ]);

                if (!mounted) return;

                const groupsById = (groupsRes?.data || []).reduce((acc, group) => {
                    acc[group.id] = group.name;
                    return acc;
                }, {});

                const templatesById = (templatesRes?.data || []).reduce((acc, template) => {
                    acc[template.id] = template.name;
                    return acc;
                }, {});

                setLookupMaps({ groupsById, templatesById });
            } catch (error) {
                setLookupMaps({ groupsById: {}, templatesById: {} });
            }
        };

        loadLookups();
        return () => { mounted = false; };
    }, []);

    const { uiStatus = {}, stats = {} } = campaignDetails;
    const reviewStatus = campaignDetails.is_active ? 'Approved' : 'Pending';
    const isPrivateCampaign = campaignDetails.privacy === 'private';
    const usersToNotify = campaignDetails.users_to_notify || [];
    const messageBody = campaignDetails.message || '--';
    const createdBy = campaignDetails.creator_name || campaignDetails.username || campaignDetails.created_by || '--';

    return (
        <Card size="small" bodyStyle={{ padding: 10 }}>
            <Row gutter={[12, 8]}>
                <Col xs={24} sm={12} md={6}>
                    <DetailField label="Campaign Name" value={campaignDetails.name} />
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <DetailField label="Review Status" value={reviewStatus} />
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <span style={styles.detailLabel}>Created By</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, minHeight: 22 }}>
                        <UserAvatar
                            name={createdBy === '--' ? 'Unknown' : createdBy}
                            userId={campaignDetails.user_id}
                            size={22}
                            showTooltip
                        />
                        <span style={{ ...styles.detailValue, minHeight: 0, fontWeight: 400 }}>
                            {createdBy}
                        </span>
                    </div>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <DetailField label="Group" value={labelFromLookup(lookupMaps.groupsById, campaignDetails.group_id)} />
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <DetailField label="No. of Contacts" value={`${stats.total || 0} contacts`} />
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <DetailField
                        label="Schedule"
                        value={(
                            <Tooltip title={formatInUserTime(campaignDetails.schedule)}>
                                <span>{formatSchedule(campaignDetails.schedule, campaignDetails.timezone)}</span>
                            </Tooltip>
                        )}
                    />
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <DetailField label="Schedule Timezone" value={campaignDetails.timezone || '--'} />
                </Col>
                {!hideTemplate && (
                    <Col xs={24} sm={12} md={6}>
                        <DetailField label="Template" value={labelFromLookup(lookupMaps.templatesById, campaignDetails.template_id)} />
                    </Col>
                )}
                {!hideCampaignStatus && (
                    <Col xs={24} sm={12} md={6}>
                        <DetailField label="Campaign Status" value={uiStatus.status || '--'} />
                    </Col>
                )}
            </Row>

            <Divider style={{ margin: '8px 0' }} />

            <Row gutter={[12, 6]}>
                <Col xs={24} md={8}>
                    <DetailField label="Private Campaign" value={isPrivateCampaign ? 'Yes' : 'No'} />
                </Col>
                <Col xs={24} md={16}>
                    <span style={styles.detailLabel}>Users to notify of responses</span>
                    {usersToNotify.length ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            {usersToNotify.map(user => (
                                <div key={user.id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <UserAvatar
                                        name={user.name || 'Unknown'}
                                        userId={user.id}
                                        size={22}
                                        showTooltip
                                    />
                                    <span style={{ ...styles.detailValue, minHeight: 0, fontWeight: 400 }}>
                                        {user.name || 'Unknown'}{user.email ? ` - ${user.email}` : ''}
                                    </span>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div style={styles.detailValue}>--</div>
                    )}
                </Col>
            </Row>

            {!hideMessage && (
                <>
                    <Divider style={{ margin: '8px 0' }} />

                    <Paragraph style={{ marginBottom: 4 }}>
                        <Text strong>Message</Text>
                        <Text type="secondary" style={{ float: 'right' }}>{`${(campaignDetails.message || '').length}/320`}</Text>
                    </Paragraph>
                    <div style={styles.messageBox}>
                        {messageBody}
                    </div>
                </>
            )}

            {!hideMessage && campaignDetails.no_slots_message ? (
                <>
                    <Paragraph style={{ margin: '8px 0 4px' }}>
                        <Text strong>Message shown when no interview slots are available</Text>
                    </Paragraph>
                    <div style={{ ...styles.messageBox, minHeight: 44 }}>
                        {campaignDetails.no_slots_message}
                    </div>
                </>
            ) : null}

            {children}
        </Card>
    );
};

export default CampaignDetailsCard;
